import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useScope } from "../context/ScopeContext";
import { usePlanPreview } from "../context/PlanPreviewContext";
import { useTheme } from "../context/ThemeContext";
import { useI18n } from "../i18n";
import { api, apiError, getImpersonation, setImpersonation } from "../lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, ArrowLeftRight, Tags, FileText, BarChart3, Download,
  Settings as SettingsIcon, CreditCard, X, LogOut, ShieldCheck, RefreshCw, Info, Share2, Camera, Users, MessageSquareWarning,
} from "lucide-react";

function TwoFABanner() {
  const { locale } = useI18n();
  const [st, setSt] = useState(null);
  useEffect(() => { api.get("/auth/mfa/status").then((r) => setSt(r.data)).catch(() => {}); }, []);
  if (!st || st.enabled || st.is_admin) return null;
  const T = {
    es: ["Recomendado: activa la verificación en dos pasos (2FA) en ", " y guarda tus códigos de recuperación.", "Configuración"],
    en: ["Recommended: enable two-factor authentication (2FA) in ", " and save your recovery codes.", "Settings"],
    fr: ["Recommandé : activez la vérification en deux étapes (2FA) dans ", " et enregistrez vos codes de récupération.", "Réglages"],
    pt: ["Recomendado: ative a verificação em dois passos (2FA) em ", " e guarde os seus códigos de recuperação.", "Definições"],
    it: ["Consigliato: attiva la verifica in due passaggi (2FA) in ", " e salva i tuoi codici di recupero.", "Impostazioni"],
  };
  const [pre, post, cfg] = T[locale] || T.es;
  return (
    <div data-testid="twofa-banner" style={{ background: "#FFF4E0", color: "#8a5a00", padding: "6px 12px", fontSize: 13, textAlign: "center" }}>
      {pre}<Link to="/settings" style={{ textDecoration: "underline", fontWeight: 700 }}>{cfg}</Link>{post}
    </div>
  );
}

const NAV = [
  { to: "/dashboard", key: "nav.panel", icon: LayoutDashboard, id: "dashboard" },
  { to: "/movements", key: "nav.movimientos", icon: ArrowLeftRight, id: "movements" },
  { to: "/categories", key: "nav.categorias", icon: Tags, id: "categories" },
  { to: "/contracts", key: "nav.contratos", icon: FileText, id: "contracts" },
  { to: "/charts", key: "nav.graficos", icon: BarChart3, id: "charts" },
  { to: "/exports", key: "nav.exportaciones", icon: Download, id: "exports" },
  { to: "/settings", key: "nav.configuracion", icon: SettingsIcon, id: "settings" },
  { to: "/subscription", key: "nav.suscripcion", icon: CreditCard, id: "subscription" },
];

function ScopeSwitch() {
  const { scope, setScope } = useScope();
  const { t } = useI18n();
  const opt = [
    { v: "personal", label: t("scope.personal") },
    { v: "professional", label: t("scope.professional") },
    { v: "all", label: t("scope.all") },
  ];
  return (
    <div className="seg" role="tablist" aria-label={t("mv.scope")}>
      {opt.map((o) => (
        <button
          key={o.v}
          data-testid={`scope-switch-${o.v}`}
          className={scope === o.v ? "on" : ""}
          aria-selected={scope === o.v}
          onClick={() => setScope(o.v)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function AppLayout() {
  const { user, workspace, logout, refreshMe } = useAuth();
  const { previewPlan, setPreviewPlan } = usePlanPreview();
  const { locale, setLocale, t } = useI18n();
  const { toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [imp, setImp] = useState(getImpersonation());
  const [reportOpen, setReportOpen] = useState(false);
  const [reportMsg, setReportMsg] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const close = () => setOpen(false);

  const RL = {
    tip: { es: "Estás usando una versión beta. Si algo falla, usa 'Reportar un problema'.", en: "You're using a beta version. If something breaks, use 'Report a problem'.", fr: "Vous utilisez une version bêta. En cas de problème, utilisez « Signaler un problème ».", pt: "Estás a usar uma versão beta. Se algo falhar, usa 'Reportar um problema'.", it: "Stai usando una versione beta. Se qualcosa non va, usa 'Segnala un problema'." },
    report: { es: "Reportar un problema", en: "Report a problem", fr: "Signaler un problème", pt: "Reportar um problema", it: "Segnala un problema" },
    desc: { es: "Cuéntanos qué ha pasado. Guardaremos la página y tu plan automáticamente.", en: "Tell us what happened. We'll capture the page and your plan automatically.", fr: "Dites-nous ce qui s'est passé. Nous enregistrons la page et votre offre automatiquement.", pt: "Diz-nos o que aconteceu. Guardamos a página e o teu plano automaticamente.", it: "Raccontaci cosa è successo. Salviamo la pagina e il tuo piano automaticamente." },
    ph: { es: "Describe el problema…", en: "Describe the problem…", fr: "Décrivez le problème…", pt: "Descreve o problema…", it: "Descrivi il problema…" },
    send: { es: "Enviar", en: "Send", fr: "Envoyer", pt: "Enviar", it: "Invia" },
    cancel: { es: "Cancelar", en: "Cancel", fr: "Annuler", pt: "Cancelar", it: "Annulla" },
    sent: { es: "¡Gracias! Hemos recibido tu reporte.", en: "Thanks! We received your report.", fr: "Merci ! Nous avons reçu votre signalement.", pt: "Obrigado! Recebemos o teu reporte.", it: "Grazie! Abbiamo ricevuto la tua segnalazione." },
    a2faBold: { es: "Activa el 2FA", en: "Enable 2FA", fr: "Activez le 2FA", pt: "Ative o 2FA", it: "Attiva il 2FA" },
    a2faText: { es: " para proteger el panel de administración. La verificación en dos pasos es obligatoria para administradores.", en: " to protect the admin panel. Two-factor authentication is mandatory for administrators.", fr: " pour protéger le panneau d'administration. La vérification en deux étapes est obligatoire pour les administrateurs.", pt: " para proteger o painel de administração. A verificação em dois passos é obrigatória para administradores.", it: " per proteggere il pannello di amministrazione. La verifica in due passaggi è obbligatoria per gli amministratori." },
    a2faBtn: { es: "Activar ahora", en: "Enable now", fr: "Activer maintenant", pt: "Ativar agora", it: "Attiva ora" },
  };
  const rt = (k) => RL[k][locale] || RL[k].es;

  const submitReport = async () => {
    if (!reportMsg.trim()) return;
    setReportBusy(true);
    try {
      await api.post("/report-problem", { message: reportMsg.trim(), url: window.location.pathname });
      toast.success(rt("sent")); setReportOpen(false); setReportMsg("");
    } catch (e) { toast.error(apiError(e)); } finally { setReportBusy(false); }
  };

  useEffect(() => {
    const sync = () => setImp(getImpersonation());
    window.addEventListener("imp-changed", sync);
    return () => window.removeEventListener("imp-changed", sync);
  }, []);

  const exitImpersonation = async () => {
    setImpersonation(null);
    await refreshMe();
    navigate("/admin");
  };

  const onLogout = async () => { await logout(); navigate("/login"); };
  const isDemo = user?.is_demo_session;
  const isAccountant = user?.role === "accountant";
  const onReset = async () => {
    try { await api.post("/demo/reset"); toast.success(t("demo.resetDone")); window.location.reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const onExit = async () => { await logout(); navigate("/demo"); };
  const captureLabel = { es: "Subir ticket", en: "Upload receipt", fr: "Ajouter un reçu", pt: "Enviar recibo", it: "Carica scontrino" }[locale] || "Subir ticket";
  const affLabel = { es: "Afiliados", en: "Affiliates", fr: "Affiliés", pt: "Afiliados", it: "Affiliati" }[locale] || "Afiliados";

  return (
    <div className="lura-app">
      {/* Sidebar bóveda */}
      <aside className={`side${open ? " open" : ""}`} data-testid="app-sidebar">
        <div className="brand">
          <div className="brandwrap">
            <img src="/logo_mono_light.png" alt="" className="logo-mark logo-l" />
            <img src="/logo_mono_dark.png" alt="" className="logo-mark logo-d" />
            <img src="/logo_word_light.png" alt="Lura Finance" className="logo logo-l" />
            <img src="/logo_word_dark.png" alt="Lura Finance" className="logo logo-d" />
          </div>
          <button className="close" onClick={close} data-testid="mobile-menu-close" aria-label="Cerrar menú"><X size={18} /></button>
        </div>
        <div className="org">
          <div className="av">{(workspace?.name || user?.name || "L").charAt(0).toUpperCase()}</div>
          <div className="min-w-0">
            <b className="block truncate">{workspace?.name || "Lura"}</b>
            <small>{isAccountant ? t("demo.gestorBadge") : (user?.name || "")}</small>
          </div>
        </div>
        <nav className="snav">
          {NAV.map((n) => (
            <NavLink key={n.id} to={n.to} onClick={close} data-testid={`nav-link-${n.id}`}>
              <n.icon size={16} strokeWidth={1.75} /> {t(n.key)}
            </NavLink>
          ))}
          {user?.role !== "accountant" && (
            <NavLink to="/upload" onClick={close} data-testid="nav-link-capture">
              <Camera size={16} strokeWidth={1.75} /> {captureLabel}
            </NavLink>
          )}
          {user?.role !== "accountant" && (
            <NavLink to="/affiliates" onClick={close} data-testid="nav-link-affiliates">
              <Share2 size={16} strokeWidth={1.75} /> {affLabel}
            </NavLink>
          )}
          {["admin", "support", "finance"].includes(user?.role) && (
            <NavLink to="/admin" onClick={close} data-testid="nav-link-admin">
              <ShieldCheck size={16} strokeWidth={1.75} /> {user?.role === "admin" ? "Admin" : user?.role === "support" ? "Soporte" : "Finanzas"}
            </NavLink>
          )}
          <NavLink to="/advisor" onClick={close} data-testid="nav-link-advisor">
            <Users size={16} strokeWidth={1.75} /> {locale === "en" ? "Advisor" : locale === "fr" ? "Gestionnaire" : locale === "pt" ? "Gestor" : locale === "it" ? "Consulente" : "Gestor"}
          </NavLink>
        </nav>
        <div className="side-foot">
          <div className="wsname">{workspace?.name}</div>
          <button data-testid="logout-button" onClick={onLogout}><LogOut size={16} /> {t("common.logout")}</button>
        </div>
      </aside>
      <div className="scrim" onClick={close} />

      <main className="main">
        <TwoFABanner />
        <div className="topbar">
          <button className="icon-btn burger" onClick={() => setOpen(true)} data-testid="mobile-menu-open" aria-label="Menú">
            <svg viewBox="0 0 24 24"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
          </button>
          <div className="brandmini" aria-hidden="true">
            <img src="/logo_mono_light.png" alt="" className="logo-l" />
            <img src="/logo_mono_dark.png" alt="" className="logo-d" />
          </div>
          <ScopeSwitch />
          <div className="sp" />
          <span className="chip" data-testid="beta-badge" title={rt("tip")} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "var(--brass, #C98A2B)", color: "#fff", fontWeight: 700, letterSpacing: ".04em" }}>Beta</span>
          <button className="icon-btn" data-testid="report-problem-open" onClick={() => setReportOpen(true)} title={rt("report")} aria-label={rt("report")}>
            <MessageSquareWarning size={18} />
          </button>
          {isAccountant && (
            <span className="chip" style={{ display: "inline-flex" }}><ShieldCheck size={13} /> {t("demo.gestorBadge")}</span>
          )}
          <button className="icon-btn theme-ib" onClick={toggle} data-testid="theme-toggle" aria-label={t("common.theme") || "Tema"}>
            <svg className="i-moon" viewBox="0 0 24 24"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>
            <svg className="i-sun" viewBox="0 0 24 24"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
          </button>
          <select
            className="lang"
            data-testid="language-select"
            value={locale}
            onChange={(e) => { const l = e.target.value; setLocale(l); if (user?.role !== "accountant") api.put("/settings", { locale: l }).catch(() => {}); }}
            aria-label="Idioma"
          >
            <option value="es">ES</option><option value="en">EN</option><option value="fr">FR</option>
            <option value="pt">PT</option><option value="it">IT</option>
          </select>
          <div className="avatar">{(user?.name || "L").charAt(0).toUpperCase()}</div>
        </div>

        {previewPlan && (
          <div data-testid="plan-sim-banner" className="card" style={{ marginBottom: 20, padding: "12px 16px", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, borderLeft: "4px solid var(--brass, #C98A2B)" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13.5 }}>
              <ShieldCheck size={16} style={{ flex: "none", color: "var(--brass, #C98A2B)" }} />
              <span><b>Simulación admin activa</b>: estás viendo la app como plan <b>{previewPlan}</b>. Solo afecta a tu visualización.</span>
            </div>
            <button onClick={() => setPreviewPlan(null)} data-testid="plan-sim-banner-exit" className="btn sm"><RefreshCw size={14} /> Volver al normal</button>
          </div>
        )}
        {imp && (
          <div data-testid="impersonation-banner" className="card" style={{ marginBottom: 20, padding: "12px 16px", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, borderLeft: "4px solid var(--brass, #C98A2B)" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13.5 }}>
              <ShieldCheck size={16} style={{ flex: "none", color: "var(--brass, #C98A2B)" }} />
              <span><b>Vista de SOLO LECTURA</b> como {imp.name || imp.email}. No puedes modificar nada. Toda la actividad queda registrada en auditoría.</span>
            </div>
            <button onClick={exitImpersonation} data-testid="impersonation-exit-button" className="btn sm"><LogOut size={14} /> Salir de la vista</button>
          </div>
        )}
        {isDemo && (
          <div data-testid="demo-banner" className="card" style={{ marginBottom: 20, padding: "12px 16px", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13.5 }}>
              <Info size={16} style={{ marginTop: 2, flex: "none", color: "var(--brass)" }} />
              <span>{t("demo.banner")}</span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={onReset} data-testid="demo-reset-button" className="btn sm"><RefreshCw size={14} /> {t("demo.reset")}</button>
              <button onClick={onExit} data-testid="demo-exit-button" className="btn sm"><LogOut size={14} /> {t("demo.exit")}</button>
            </div>
          </div>
        )}
        {isAccountant && (
          <div className="card" style={{ marginBottom: 20, padding: "12px 16px", fontSize: 13.5 }}>{t("demo.inspection")}</div>
        )}
        {user?.role === "admin" && !user?.mfa?.enabled && (
          <div data-testid="admin-2fa-banner" className="card" style={{ marginBottom: 20, padding: "12px 16px", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, borderLeft: "4px solid var(--brass, #C98A2B)" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13.5 }}>
              <ShieldCheck size={16} style={{ flex: "none", color: "var(--brass, #C98A2B)" }} />
              <span><b>{rt("a2faBold")}</b>{rt("a2faText")}</span>
            </div>
            <Link to="/settings" data-testid="admin-2fa-banner-link" className="btn sm"><ShieldCheck size={14} /> {rt("a2faBtn")}</Link>
          </div>
        )}
        <Outlet />
      </main>

      {reportOpen && (
        <div data-testid="report-problem-modal" onClick={() => setReportOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(10,25,25,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
          <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 480, padding: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}><MessageSquareWarning size={18} style={{ color: "var(--brass, #C98A2B)" }} /> {rt("report")}</h2>
              <button className="icon-btn" data-testid="report-problem-close" onClick={() => setReportOpen(false)} aria-label={rt("cancel")}><X size={18} /></button>
            </div>
            <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 12 }}>{rt("desc")}</p>
            <textarea className="sel" data-testid="report-problem-text" value={reportMsg} onChange={(e) => setReportMsg(e.target.value)}
              placeholder={rt("ph")} rows={5} style={{ width: "100%", resize: "vertical", marginBottom: 12 }} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn sm" data-testid="report-problem-cancel" onClick={() => setReportOpen(false)}>{rt("cancel")}</button>
              <button className="btn sm primary" data-testid="report-problem-submit" onClick={submitReport} disabled={reportBusy || !reportMsg.trim()}>{rt("send")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
